import { type Action } from '@reduxjs/toolkit';
import { type Epic, combineEpics } from 'redux-observable';
import {
  EMPTY,
  catchError,
  distinctUntilChanged,
  filter,
  from,
  fromEvent,
  map,
  merge,
  mergeMap,
  startWith,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { fetchIncidentsRequest } from '@/api/events';
import { type Incident } from '@/types';
import {
  addIncident,
  createIncident,
  deleteIncident,
  editIncident,
  removeIncident,
  resetCurrentEvent,
  startRealtimeIncidents,
  updateIncident,
} from '../actions';
import { currentIncidents } from '../selectors';

// Polls liveblog-be instead of Supabase Realtime. Name kept from the Realtime
// version so actions and wiring stay unchanged.
const POLL_INTERVAL_MS = 5000;

const isChanged = (next: Incident, current: Incident) =>
  (Object.keys(next) as (keyof Incident)[]).some(
    (key) => next[key] !== current[key],
  );

// Turns a polled list into the same add/update/remove actions Realtime used to
// emit, so in-progress editor UI is patched rather than replaced.
export const diffIncidents = (current: Incident[], polled: Incident[]) => {
  const currentById = new Map(
    current.map((incident) => [incident.id, incident]),
  );
  const polledIds = new Set(polled.map(({ id }) => id));

  return [
    ...current
      .filter(({ id }) => !polledIds.has(id))
      .map(({ id }) => removeIncident({ incidentId: id })),
    // Oldest first, so each addIncident (unshift) leaves the newest on top.
    ...[...polled].reverse().flatMap((incident): Action[] => {
      const existing = currentById.get(incident.id);
      if (!existing) return [addIncident({ incident })];
      return isChanged(incident, existing)
        ? [updateIncident({ incident })]
        : [];
    }),
  ];
};

const pageVisible$ = () =>
  fromEvent(document, 'visibilitychange').pipe(
    startWith(null),
    map(() => !document.hidden),
    distinctUntilChanged(),
  );

const realtimeIncidentsEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(startRealtimeIncidents.match),
    switchMap(({ payload: { eventId } }) =>
      merge(
        // No polling while the tab is hidden; poll at once when it returns.
        pageVisible$().pipe(
          switchMap((visible) =>
            visible ? timer(0, POLL_INTERVAL_MS) : EMPTY,
          ),
        ),
        // Editors see their own change immediately, not on the next tick.
        action$.pipe(
          filter(
            (action) =>
              createIncident.succeeded.match(action) ||
              editIncident.succeeded.match(action) ||
              deleteIncident.succeeded.match(action),
          ),
        ),
      ).pipe(
        switchMap(() =>
          from(fetchIncidentsRequest({ eventId })).pipe(
            catchError(() => EMPTY),
          ),
        ),
        mergeMap((incidents) =>
          diffIncidents(currentIncidents(state$.value), incidents),
        ),
        takeUntil(action$.pipe(filter(resetCurrentEvent.match))),
      ),
    ),
  );

export default combineEpics(realtimeIncidentsEpic);
